import yaml from 'js-yaml';
import imm from 'immutable';
import diff from 'immutablediff';
import patch from 'immutablepatch';

import {browserHistory} from 'react-router';

import {download} from '../file_creator';
import {prepare, submit} from '../request_client';
import {http_methods, resolveUrls} from '../utils';
import {get_tags} from '../spec_utils';

import _ from 'lodash';
import url from 'url';

const parse_url = url.parse(window.location.href, true);
const edit_mode = _.has(parse_url.query, 'edit') || false;
const full_mode = _.has(parse_url.query, 'full') || false;

class SpecStorage {
  constructor(local_storage) {
    this._local_storage = local_storage;
    this._specs = local_storage ? JSON.parse(window.localStorage.getItem('specs') || '{}') : {};
  }

  getSpecs() {
    return _.mapValues(this._specs, yaml.safeLoad);
  }

  getRawSpecs() {
    return this._specs;
  }

  persistSpecs(override) {
    if (this._local_storage || override) {
      window.localStorage.setItem('specs', JSON.stringify(this._specs));
    }
  }

  setSpec(api, raw) {
    this._specs[api] = raw;
    this.persistSpecs();
  }

  deleteSpec(api) {
    delete this._specs[api];
    this.persistSpecs();
  }

  async loadSpecsFromAPI() {
    const query = full_mode ? {full: true} : {};
    const request = await prepare({
      route: '/api/v1/specs',
      base_url: `${resolveUrls(location.hostname, 'api$$suffix$$')}:${location.port}`,
      method: 'get',
      query
    });
    const {body} = await submit(request);
    const {specs} = body;

    for (const spec of specs) {
      const req = await prepare({
        route: `/api/v1/specs/${spec}`,
        base_url: `${resolveUrls(location.hostname, 'api$$suffix$$')}:${location.port}`,
        method: 'get',
        query
      });
      const response = await submit(req);
      if (response.body) {
        store.dispatch({type: 'LOAD_SPEC', data: response.body});
      }
      spec_storage.persistSpecs(true);
    }
  }
}

const spec_storage = new SpecStorage(edit_mode);

// async initialization
spec_storage.loadSpecsFromAPI();

const update_tags = spec => {
  setTimeout(() => {
    if (!store) return;
    const tags = get_tags(spec);
    store.dispatch({type: 'ADD_TAGS', data: tags});
  });
};

export default {
  CONSTRUCT() {
    return {
      edit_mode,
      full_mode,
      selected: {
        route: '',
        api: '',
        method: '',
        path: []
      },
      current_spec: null,
      collapsed: false,
      specs: spec_storage.getSpecs(),
      raw: spec_storage.getRawSpecs()
    };
  },
  ADD_SPEC(state, spec) {
    return state.update('specs', specs => specs.push(spec));
  },
  ADD_EXAMPLE(state, example) {
    // TODO: keep examples separate from spec

    const api = state.getIn(['selected', 'path', 0]);
    const lookup = ['routes', ...example.request.path, 'methods', example.request.method, 'examples'];
    const {request, response} = example;

    const req = {
      params: {
        header: request.headers,
        query: _.mapValues(request.query, val => val.toString())
      }
    };

    if (request.body) {
      req.body = request.body;
    }

    const spec = state.get('current_spec');
    const examples = _.get(spec, lookup, []);
    examples.push({
      summary: 'test',
      description: 'test',
      request: req
    });

    const raw = yaml.dump(doc, {lineWidth: '1000000'});
    state = state.setIn(['raw', api], raw);

    spec_storage.setSpec(api, raw);

    return state.setIn(['specs', api], imm.fromJS(spec));
  },
  TOGGLE_ROUTE_LIST(state) {
    return state.update('collapsed', val => !val);
  },
  ROUTE_UPDATE(state, {api, route, method}) {
    let path = [api || state.getIn(['selected', 'api'])];
    if (route) {
      path = path.concat(route === '/' ? ['routes', '/'] : ['routes', ...route.split('/').map(part => `/${part}`)]);
    }
    if (method) {
      path = path.concat(['methods', method]);
    }

    if (api !== state.getIn(['selected', 'api'])) {
      const spec = state.getIn(['specs', api], null);
      if (spec) {
        const current_spec = spec.toJS();
        update_tags(current_spec);
        state = state.set('current_spec', current_spec);
      }
    }

    return state.set('selected', imm.fromJS({api, route, method, path}));
  },
  SAVE_SPEC(state) {
    const api = state.getIn(['selected', 'path', 0]);
    download(`${api}.yaml`, state.getIn(['raw', api]), 'text/x-yaml');
    return state;
  },
  LOAD_SPEC(state, data) {
    const spec = yaml.safeLoad(data);
    state = state.set('current_spec', spec);

    const api = spec.info.title.toLowerCase();
    state = state.setIn(['raw', api], data);

    spec_storage.setSpec(api, data);

    setTimeout(() => {
      browserHistory.push(`/${api}`);
    });

    return state.setIn(['specs', api], imm.fromJS(spec));
  },
  LOAD_SPECS_FROM_API(state) {
    spec_storage.loadSpecsFromAPI();
    return state;
  },
  REMOVE_API(state, data) {
    const {item, menu} = data;
    const api = menu.path.substr(1);
    const current_api = state.getIn(['selected', 'path', 0]);

    spec_storage.deleteSpec(api);

    if (current_api === api) {
      setTimeout(() => {
        browserHistory.push('/');
      });
    }

    return state.deleteIn(['specs', api]).deleteIn(['raw', api]);
  },
  UPDATE_SPEC(state, {raw, doc}) {
    const api = state.getIn(['selected', 'path', 0]);
    state = state.setIn(['raw', api], raw);

    spec_storage.setSpec(api, raw);

    // doc is currently invalid...
    if (!doc) {
      return state;
    }

    state = state.set('current_spec', doc);

    return state.updateIn(['specs', api], spec => {
      const _diff = diff(spec, imm.fromJS(doc));
      return patch(spec, _diff);
    });
  }
};
